import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import type FVector from '@data/geometry/FVector';
import GeomSerializable, { GeomHeaderSchema } from './GeomSerializable';
import { GeometryChunkId } from './GeometryChunkId';

type BaseSchema = {
    version: number;
};

type SchemaV5 = BaseSchema & {
    numberOfRoads: number;
    roads: { points: FVector[]; emission: number }[];
};

type SchemaV14 = BaseSchema & {
    numberOfRoads: number;
    roads: { points: FVector[]; emission: number; primaryKey: number }[];
};

type PreviousSchema = SchemaV5 | SchemaV14;
export type LatestRoadsSchema = SchemaV14;

class RoadsSerializer extends GeomSerializable<PreviousSchema, LatestRoadsSchema> {
    protected readonly expectedChunkId = GeometryChunkId.Roads;
    private static readonly LATEST_VERSION = 8;

    protected migrateToLatest(anyPrevious: PreviousSchema): LatestRoadsSchema {
        let migrated: PreviousSchema = anyPrevious;
        while (migrated.version < RoadsSerializer.LATEST_VERSION) {
            const migrate = this.getMigrationForVersion(migrated.version);
            if (!migrate) {
                throw new Error(`Missing Roads migration for version ${migrated.version}`);
            }
            migrated = migrate(migrated);
        }
        return migrated as LatestRoadsSchema;
    }

    protected deserializeContent(header: GeomHeaderSchema, reader: BinaryReader): LatestRoadsSchema {
        let parsed: PreviousSchema;
        if (header.version >= 5 && header.version < 8) {
            parsed = this.deserializeV5(header, reader);
        } else if (header.version >= 8) {
            parsed = this.deserializeV14(header, reader);
        } else {
            throw new Error(`Unsupported Roads version: ${header.version}. Update your files`);
        }
        return this.migrateToLatest(parsed);
    }

    private getMigrationForVersion(version: number): ((schema: PreviousSchema) => PreviousSchema) | undefined {
        if (version < 8) return (schema) => this.migrateV5ToV14(schema as SchemaV5);
        return undefined;
    }

    private migrateV5ToV14(schema: SchemaV5): SchemaV14 {
        return {
            version: 8,
            numberOfRoads: schema.numberOfRoads,
            roads: schema.roads.map((road) => ({ ...road, primaryKey: 0 })),
        };
    }

    private deserializeV5(header: GeomHeaderSchema, reader: BinaryReader): SchemaV5 {
        const numberOfRoads = reader.readUInt16();
        const roads: SchemaV5['roads'] = [];

        for (let r = 0; r < numberOfRoads; r++) {
            const pointCount = reader.readUInt16();
            const points: FVector[] = [];
            for (let p = 0; p < pointCount; p++) {
                const point = reader.readVec3();
                points.push(point);
            }
            const emission = reader.readFloat32();
            roads.push({ points, emission });
        }
        return { version: header.version, numberOfRoads, roads };
    }

    private deserializeV14(header: GeomHeaderSchema, reader: BinaryReader): SchemaV14 {
        const numberOfRoads = reader.readUInt16();
        const roads: SchemaV14['roads'] = [];

        for (let r = 0; r < numberOfRoads; r++) {
            const pointCount = reader.readUInt16();
            const points: FVector[] = [];
            for (let p = 0; p < pointCount; p++) {
                const point = reader.readVec3();
                points.push(point);
            }
            const emission = reader.readFloat32();
            const primaryKey = reader.readUInt32();
            roads.push({ points, emission, primaryKey: primaryKey });
        }

        return { version: header.version, numberOfRoads, roads };
    }
}

export default RoadsSerializer;
